import React from 'react';
import {
    ResponsiveContainer,
    LineChart,
    Line,
    BarChart,
    Bar,
    PieChart,
    Pie,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    Cell
} from 'recharts';
import {
    Box,
    Paper,
    Typography,
    Grid,
    useTheme,
    Tab,
    Tabs,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Alert
} from '@mui/material';
import { format, subDays, startOfDay } from 'date-fns';

// Custom tooltip for charts
const CustomTooltip = ({ active, payload, label, valueLabel }) => {
    if (active && payload && payload.length) {
        return (
            <Paper sx={{ p: 1.5 }}>
                <Typography variant="body2" color="textSecondary">
                    {label}
                </Typography>
                <Typography variant="body1" color="primary" sx={{ mt: 0.5 }}>
                    {`${valueLabel}: ${payload[0].value}`}
                </Typography>
            </Paper>
        );
    }
    return null;
};

const chartStyles = {
    chart: {
        height: 400,
        marginBottom: 20
    },
    xAxis: {
        labels: {
            style: {
                fontSize: '12px',
                fontFamily: 'inherit'
            },
            y: 20  // Adjust label position
        }
    },
    legend: {
        verticalAlign: 'top',
        y: -10
    }
};

const ReadingAnalytics = ({ data }) => {
    const theme = useTheme();
    const [timeRange, setTimeRange] = React.useState('all');
    const [activeTab, setActiveTab] = React.useState(0);

    // Validate data is an array and not empty
    const isValidData = Array.isArray(data) && data.length > 0;
    
    // Log data for debugging
    React.useEffect(() => {
        console.log('ReadingAnalytics received data:', data);
    }, [data]);

    // Process data for different time ranges
    const getFilteredData = (days) => {
        if (!isValidData) return [];
        
        console.log('Filtering data for time range:', days);
        
        // If 'all' is selected, return all data
        if (days === 'all') {
            console.log('Returning all data:', data.length, 'records');
            return data;
        }
        
        const cutoffDate = startOfDay(subDays(new Date(), days));
        console.log('Cutoff date:', cutoffDate);
        
        const filtered = data.filter(item => {
            const itemDate = new Date(item.date);
            const result = itemDate >= cutoffDate;
            return result;
        });
        
        console.log('Filtered data:', filtered.length, 'records');
        return filtered;
    };

    // Prepare data for different charts
    const prepareTimeSeriesData = () => {
        // Use different days based on timeRange
        const days = timeRange === 'week' ? 7 : 
                    timeRange === 'month' ? 30 : 
                    timeRange === 'quarter' ? 90 : 
                    'all';
                    
        const filteredData = getFilteredData(days);
        
        if (filteredData.length === 0) return [];
        
        return filteredData.map(item => ({
            date: format(new Date(item.date), 'MMM dd'),
            wpm: item.readingSpeed || 0,  // Map readingSpeed to wpm
            words: item.wordsRead || 0,   // Map wordsRead to words
            time: item.readingTime || 0   // Keep readingTime as time
        }));
    };

    const prepareCategoryData = () => {
        if (!isValidData) return [];
        
        // Use different days based on timeRange
        const days = timeRange === 'week' ? 7 : 
                    timeRange === 'month' ? 30 : 
                    timeRange === 'quarter' ? 90 : 
                    'all';
                    
        const filteredData = getFilteredData(days);
        
        if (filteredData.length === 0) return [];
        
        const categoryStats = new Map();
        let totalWords = 0;
        
        // First pass: collect total words and initialize categories
        filteredData.forEach(item => {
            const category = item.category || 'Uncategorized';
            const wordsRead = item.wordsRead || 0;
            
            totalWords += wordsRead;
            
            if (!categoryStats.has(category)) {
                categoryStats.set(category, {
                    name: category,
                    articles: 0,
                    words: 0,
                    percentage: 0
                });
            }
            
            const stats = categoryStats.get(category);
            stats.articles++;
            stats.words += wordsRead;
        });
        
        // Second pass: calculate percentages and combine small categories
        let otherWords = 0;
        const THRESHOLD = 0.03; // 3% threshold for "Other" category
        
        const processedStats = [];
        
        categoryStats.forEach(stats => {
            stats.percentage = totalWords > 0 ? Math.round((stats.words / totalWords) * 100) : 0;
            
            // If category is less than threshold, add to "Other"
            if (totalWords > 0 && stats.words / totalWords < THRESHOLD) {
                otherWords += stats.words;
            } else {
                processedStats.push(stats);
            }
        });
        
        // Add "Other" category if needed
        if (otherWords > 0) {
            const otherPercentage = totalWords > 0 ? Math.round((otherWords / totalWords) * 100) : 0;
            processedStats.push({
                name: 'Other',
                articles: 0, // We don't track this for the "Other" category
                words: otherWords,
                percentage: otherPercentage
            });
        }
        
        // Sort by words read (descending)
        return processedStats.sort((a, b) => b.words - a.words);
    };

    const COLORS = [
        '#2196F3', // Blue (Fiction)
        '#E91E63', // Pink (Technical)
        '#4CAF50', // Green (Business)
        '#FF9800', // Orange (Academic)
        '#F44336', // Red (News)
        '#00BCD4', // Cyan (Sports)
        '#9C27B0', // Purple (Philosophy)
        '#FF4081', // Pink (Health)
        '#795548', // Brown (History)
        '#FF9100', // Amber (Science)
        '#C2185B', // Dark Pink (Food)
        '#607D8B'  // Blue Grey (Uncategorized)
    ];
    
    // Custom pie chart label renderer to prevent overlapping
    const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index, name, percentage }) => {
        // Only show labels for categories with percentage >= 5%
        if (percentage < 5) return null;
        
        const RADIAN = Math.PI / 180;
        // Position the label further from the pie
        const radius = outerRadius * 1.2;
        const x = cx + radius * Math.cos(-midAngle * RADIAN);
        const y = cy + radius * Math.sin(-midAngle * RADIAN);
        
        return (
            <text 
                x={x} 
                y={y} 
                fill={COLORS[index % COLORS.length]}
                textAnchor={x > cx ? 'start' : 'end'} 
                dominantBaseline="central"
                fontWeight="bold"
                fontSize="14px"
            >
                {`${name} ${percentage}%`}
            </text>
        );
    };

    // Check if we have any data to display
    const timeSeriesData = prepareTimeSeriesData();
    const categoryData = prepareCategoryData();
    const hasTimeSeriesData = timeSeriesData.length > 0;
    const hasCategoryData = categoryData.length > 0;
    
    // Log prepared data for debugging
    React.useEffect(() => {
        console.log('Prepared time series data:', timeSeriesData);
        console.log('Prepared category data:', categoryData);
    }, [timeSeriesData, categoryData]);
    
    // Handle time range change
    const handleTimeRangeChange = (event) => {
        const newTimeRange = event.target.value;
        console.log('Time range changed to:', newTimeRange);
        setTimeRange(newTimeRange);
    };

    return (
        <Box sx={{ mt: 4 }}>
            <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Detailed Analytics
                </Typography>
                
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
                        <Tab label="READING SPEED" value={0} />
                        <Tab label="VOLUME" value={1} />
                        <Tab label="CATEGORIES" value={2} />
                    </Tabs>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <Select 
                            value={timeRange} 
                            onChange={handleTimeRangeChange}
                            displayEmpty
                        >
                            <MenuItem value="all">All Time</MenuItem>
                            <MenuItem value="week">Last Week</MenuItem>
                            <MenuItem value="month">Last Month</MenuItem>
                            <MenuItem value="quarter">Last 3 Months</MenuItem>
                        </Select>
                    </FormControl>
                </Box>

                {!isValidData && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        No reading history data available. Complete some reading sessions to see your analytics.
                    </Alert>
                )}

                {activeTab === 0 && (
                    <Box sx={chartStyles.chart}>
                        {hasTimeSeriesData ? (
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={timeSeriesData}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="date" />
                                    <YAxis />
                                    <Tooltip content={<CustomTooltip valueLabel="WPM" />} />
                                    <Legend />
                                    <Line
                                        type="monotone"
                                        dataKey="wpm"
                                        stroke={theme.palette.primary.main}
                                        strokeWidth={2}
                                        dot={{ r: 4 }}
                                        name="Reading Speed (WPM)"
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        ) : (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                <Typography variant="body1" color="textSecondary">
                                    No reading speed data available for this time period.
                                </Typography>
                            </Box>
                        )}
                    </Box>
                )}

                {activeTab === 1 && (
                    <Box sx={chartStyles.chart}>
                        {hasTimeSeriesData ? (
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={timeSeriesData}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="date" />
                                    <YAxis yAxisId="left" orientation="left" stroke={theme.palette.primary.main} />
                                    <YAxis yAxisId="right" orientation="right" stroke={theme.palette.secondary.main} />
                                    <Tooltip />
                                    <Legend />
                                    <Bar
                                        yAxisId="left"
                                        dataKey="words"
                                        name="Words Read"
                                        fill={theme.palette.primary.main}
                                    />
                                    <Bar
                                        yAxisId="right"
                                        dataKey="time"
                                        name="Reading Time (min)"
                                        fill={theme.palette.secondary.main}
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        ) : (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                <Typography variant="body1" color="textSecondary">
                                    No volume data available for this time period.
                                </Typography>
                            </Box>
                        )}
                    </Box>
                )}

                {activeTab === 2 && (
                    <Box sx={chartStyles.chart}>
                        {hasCategoryData ? (
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart margin={{ top: 20, right: 30, left: 30, bottom: 20 }}>
                                    <Pie
                                        data={categoryData}
                                        dataKey="words"
                                        nameKey="name"
                                        cx="50%"
                                        cy="50%"
                                        outerRadius={140}
                                        innerRadius={30}
                                        paddingAngle={2}
                                        labelLine={false}
                                        label={renderCustomizedLabel}
                                    >
                                        {categoryData.map((entry, index) => (
                                            <Cell 
                                                key={`cell-${index}`} 
                                                fill={COLORS[index % COLORS.length]} 
                                                stroke="#fff"
                                                strokeWidth={1}
                                            />
                                        ))}
                                    </Pie>
                                    <Tooltip 
                                        formatter={(value, name, props) => [
                                            `${value.toLocaleString()} words (${props.payload.percentage}%)`, 
                                            props.payload.name
                                        ]}
                                        contentStyle={{
                                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                            border: '1px solid #ccc',
                                            borderRadius: '4px',
                                            padding: '8px'
                                        }}
                                    />
                                    <Legend 
                                        layout="horizontal" 
                                        verticalAlign="bottom" 
                                        align="center"
                                        formatter={(value, entry, index) => value}
                                        wrapperStyle={{ paddingTop: '30px' }}
                                        iconSize={10}
                                        iconType="circle"
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                        ) : (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                <Typography variant="body1" color="textSecondary">
                                    No category data available for this time period.
                                </Typography>
                            </Box>
                        )}
                    </Box>
                )}
            </Paper>
        </Box>
    );
};

export default ReadingAnalytics;
